import { DatePipe, NgIf, NgClass, NgFor, AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {DeviceService} from '@app/core/services/device.service';
import {RouterService} from '@app/core/services/router.service';
import {PersistentTabService} from '@app/core/state/persistent-tab/persistent-tab.service';
import {PersistentTabStore} from '@app/core/state/persistent-tab/persistent-tab.store';
import {StringUtil} from '@app/core/util/string.util';
import {FormatNumberPipe} from '@app/shared/pipe/format-number-pipe';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {
  Fahrzeug,
  GenehmigungPolizzenSondertarif,
  GenehmigungSchadenEinsicht,
  Haushalt,
  KeyValue,
  Kundenverbund,
  Person,
  Polizze,
  Schaden,
  Schadenbeteiligung,
  Vertreter,
  ZulassungResult
} from '@pezi/entities';
import {
  GenehmigungPolizzenEinsicht
} from '@pezi/entities/dist/src/main/ts/models/generated/genehmigung/genehmigungpolizzeneinsicht';
import {Observable} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import { MatIcon } from '@angular/material/icon';
import { TextSmallDirective } from '../../core/directive/text-small.directive';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { ActivePoliceState } from '../../core/state/active-police';
import { LoaderComponent } from '../loader/loader.component';

@UntilDestroy()
@Component({
  selector:        'app-tab-bar',
  templateUrl:     './tab-bar.component.html',
  styleUrls:       ['./tab-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone:      true,
  imports: [
    NgIf,
    NgClass,
    MatIcon,
    NgFor,
    TextSmallDirective,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    AsyncPipe,
    LoaderComponent
  ]
})
export class TabBarComponent implements
                                        AfterViewInit {

  public activeTabId: string;
  hasTabs$ = this.persistentTabService.getTabs$()
    .pipe(map(tabs => tabs && tabs.length));

  public dragActive: Tab;
  public overflow = false;
  public scrollStart = false;
  public scrollEnd = false;
  public dragTargetIndex: number;
  public datePipe = new DatePipe('de-AT');

  @ViewChildren('tab', {read: ElementRef}) viewChildrenTab: QueryList<ElementRef>;

  @ViewChild('scroller')
  private el: ElementRef;

  private formatNumberPipe = new FormatNumberPipe();

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute, public deviceService: DeviceService,
              public persistentTabService: PersistentTabService,
              private persistentTabStore: PersistentTabStore,
              private cd: ChangeDetectorRef, private routerService: RouterService) {

    const onNavEnd = this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd));
    onNavEnd
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(navigationEnd => {

        if (navigationEnd && navigationEnd.url) {
          let currentTabIdByUrl = this.persistentTabService.getTabIDForURL(navigationEnd.url);
          if (currentTabIdByUrl) {
            const tab = this.persistentTabService.getTab(currentTabIdByUrl);
            if (tab) {
              // update url from last navigation
              this.persistentTabService.addTab({
                ...tab
              }, false, navigationEnd.url);
            }
            this.persistentTabService.setActive(currentTabIdByUrl);
          } else {
            this.persistentTabService.setActive(null);
          }
        } else {
          this.persistentTabService.setActive(null);
        }
      });
    this.deviceService
      .getDeviceSubject()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.cd.markForCheck();
      });

    this.persistentTabService.getActiveTab$()
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(tab => {
        if (tab) {
          this.activeTabId = tab.config.idURL;
          if (!this.deviceService.isHandset()) {
            this.scrollToTab(tab);
          }
        } else {
          this.activeTabId = null;
        }
        this.cd.markForCheck();
      });

  }

  ngAfterViewInit(): void {
    this.viewChildrenTab.changes.pipe(
        untilDestroyed(this)
      )
      .subscribe((change) => {
        this.checkScrollBar();
      });
    this.checkScrollBar();
    this.cd.markForCheck();
  }

  public closeTabFromMenu(evt: MouseEvent, tab: Tab): void {
    evt.stopPropagation();
    this.removeTab(tab);
  }

  checkScrollBar() {
    setTimeout(() => {
      if (this.el) {
        this.overflow = this.el.nativeElement.scrollWidth > this.el.nativeElement.offsetWidth;
        this.scrollStart = this.el.nativeElement.scrollLeft === 0;
        this.scrollEnd = this.el.nativeElement.scrollLeft + this.el.nativeElement.offsetWidth >= this.el.nativeElement.scrollWidth;
        //console.log(`overflow: ${this.overflow} / scrollStart: ${this.scrollStart} / scrollEnd: ${this.scrollEnd}`);
        //console.log(`scrollWidth ${this.el.nativeElement.scrollWidth} / offsetWidth ${this.el.nativeElement.offsetWidth}`)
        this.cd.markForCheck();
      }
    });
  }

  scrollToTab(tab: Tab) {
    setTimeout(() => {
      const element = document.getElementById(tab.config.idURL);
      if (element) {
        element.scrollIntoView();
      } else {
        console.log('did not find element with id: ' + tab.config.idURL);
      }
    });
  }

  scrollTab(x: number) {
    if (this.el) {
      this.el.nativeElement.scrollLeft += 40 * x;
      this.checkScrollBar();
    }
  }

  public getDropdownTabs(): Observable<Tab[]> {
    return this.persistentTabService.getTabs$()
      .pipe(
        untilDestroyed(this),
        map(tabs => tabs.filter(tab => (!this.persistentTabService.getActiveTab() || tab.config.idURL !== this.persistentTabService.getActiveTab().config.idURL))));
  }

  /**
   * On initial Tab create tab.url is not available -> Fallback used
   * @param tab
   */
  public navigateTab(tab: Tab): void {
    this.router.navigateByUrl(tab.config.url ? tab.config.url : tab.config.fallbackURL);
  }

  public showTabDropdown(): Observable<boolean> {
    return this.persistentTabService.getTabs$()
      .pipe(
        untilDestroyed(this),
        map(tabs => {
          if (tabs) {
            return tabs.length > 1 || (!this.activeTabId && tabs.length === 1 && this.deviceService.isHandset());
          }
          return false;
        }));
  }

  public closeAllTabs(): void {
    this.persistentTabService.clearPersistentTabStore();
    this.router.navigate(['/']);
  }

  public removeTab(tab: Tab): void {
    if (this.persistentTabService.isActive(tab)) {
      const tabs = this.persistentTabService.getTabs();
      const id = tabs.findIndex(t => t.config.idURL === tab.config.idURL);

      // remove tab from store and local variable
      tabs.splice(id, 1);
      this.persistentTabService.removeTab(tab);

      if (tabs.length === 0) {
        // letzter Tab entfernt -> go home
        this.router.navigate(['/']);
      } else {
        const tabId = id < tabs.length ? id : tabs.length - 1;
        this.navigateTab(tabs[tabId]);
      }
    } else {
      this.persistentTabService.removeTab(tab);
    }
  }

  isDropZone($event: any) {
    $event.preventDefault();
  }

  dragTabStart($event: DragEvent, tab: Tab) {
    $event.dataTransfer.setData('text', tab.config.idURL);
    this.dragActive = tab;
  }

  dropTab($event: DragEvent, targetTab: Tab) {
    $event.preventDefault();

    const currentTabs = this.persistentTabService.getTabs();
    const indexOfActiveTab = currentTabs.findIndex(v => v.config.idURL === this.dragActive.config.idURL);
    const indexOfTargetTab = currentTabs.findIndex(v => v.config.idURL === targetTab.config.idURL);
    this.persistentTabService.moveTab(indexOfActiveTab, indexOfTargetTab);

    this.navigateTab(this.dragActive);
    this.dragActive = null;
  }

}

export interface Tab {
  data?: Polizze | Person | Schaden | Fahrzeug | Haushalt | Kundenverbund | Vertreter | ZulassungResult | ActivePoliceState | any;
  config: TabConfig;
}

export interface TabConfig {
  idURL: string;
  url?: string;
  icon: string;
  label: string;
  sublabel?: string;
  fallbackURL: string;
  loading?: boolean;
}
